import React, { useCallback, useEffect, useState } from 'react';
import { Monitor, Sun, Moon } from '@phosphor-icons/react';

const STORAGE_KEY = 'collections_theme';

const OPTIONS = [
  { value: 'system', label: 'Match system theme', Icon: Monitor },
  { value: 'light', label: 'Light theme', Icon: Sun },
  { value: 'dark', label: 'Dark theme', Icon: Moon },
];

function readStoredTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Three-way theme control: system, light, dark.
 *
 * 'system' clears the data-theme attribute so the @media (prefers-color-scheme)
 * block in index.css takes over; the explicit values set it so they win in both
 * directions. index.html reapplies the stored value before first paint, which is
 * why the storage key is duplicated there.
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    try {
      if (theme === 'system') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, theme);
      }
    } catch {
      // Private browsing or blocked storage: the theme still applies for this session.
    }
  }, [theme]);

  const handleSelect = useCallback((value) => () => setTheme(value), []);

  return (
    <div className="theme-toggle" role="group" aria-label="Theme">
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          className="theme-toggle-option"
          onClick={handleSelect(value)}
          aria-pressed={theme === value}
          aria-label={label}
          title={label}
        >
          <Icon size={16} />
        </button>
      ))}
    </div>
  );
}
